import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageForm from '../../../templates/PageForm';
import CollectionPolicyEditor from './components/CollectionPolicyEditor';
import collectionApi, { FieldError, Meta } from '../../../../services/collectionApi';
import { CollectionPolicy } from './collectionModel';
import { withRuleIds } from './useCollectionSettings';
import { reportCollectionError } from './collectionErrors';
import { collectionPoliciesPath } from './collectionRoutes';

interface CollectionPolicyPageProps {
    mode: 'new' | 'edit';
}

const newPolicy = (): CollectionPolicy => ({
    id: '', name: '', description: '', status: 'ACTIVE', statusReason: null, schedules: { preventive: null, overdue: null }, rules: [],
});

const CollectionPolicyPage = ({ mode }: CollectionPolicyPageProps) => {
    const navigate = useNavigate();
    const { policyId } = useParams();
    const [policy, setPolicy] = useState<CollectionPolicy | null>(mode === 'new' ? newPolicy() : null);
    const [meta, setMeta] = useState<Meta>({});
    const [loading, setLoading] = useState(true);
    const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
    const [saving, setSaving] = useState(false);
    const backToList = () => navigate(collectionPoliciesPath());

    useEffect(() => {
        let active = true;
        setLoading(true);
        const request = mode === 'edit' && policyId ? collectionApi.getPolicy(policyId) : Promise.resolve(null);
        request
            .then(policyResponse => {
                if (!active || !policyResponse) return;
                setMeta(policyResponse.meta || {});
                setPolicy(withRuleIds(policyResponse.data));
            })
            .catch(error => { if (active) reportCollectionError(error, 'Não foi possível carregar a política.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [mode, policyId]);

    const save = async (draft: CollectionPolicy) => {
        if (!draft.name.trim()) return;
        setSaving(true);
        setFieldErrors([]);
        try {
            const { data } = await collectionApi.savePolicy({ ...draft, name: draft.name.trim() });
            toast.success(`Política ${data.name} salva`);
            backToList();
        } catch (error) {
            reportCollectionError(error, 'Não foi possível salvar a política.', setFieldErrors);
        } finally {
            setSaving(false);
        }
    };

    const title = mode === 'new' ? 'Nova política de cobrança' : policy?.name || 'Política de cobrança';
    const policyLabel = policy?.serialId ? `Política #${policy.serialId}` : 'Política';
    const breadcrumb = [
        { label: 'Políticas', url: collectionPoliciesPath() },
        ...(mode === 'new'
            ? [{ label: 'Nova política', url: '#' }]
            : [{ label: policyLabel, url: '#' }, { label: 'Edição', url: '#' }]),
    ];

    return (
        <PageForm pageTitle={title} apiVersion={meta.apiVersion || ''} isLoading={loading} breadcrumb={breadcrumb}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button type="button" className="text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded"
                        title="Cancelar" disabled={saving} onClick={backToList}>
                        Cancelar
                    </button>
                    <button type="button" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                        title="Salvar política" disabled={!policy || !policy.name.trim() || saving || loading}
                        onClick={() => policy && save(policy)}>
                        Salvar
                    </button>
                </div>
            )}>
            <div className="py-4">
                {policy && !loading && (
                    <CollectionPolicyEditor policy={policy} fieldErrors={fieldErrors} onChange={setPolicy} />
                )}
            </div>
        </PageForm>
    );
};

export default CollectionPolicyPage;
